import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { UserPlus, KeyRound, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_USER = { name: '', email: '', password: '', role: 'user', subscription_status: 'inactive' };
const EMPTY_PW = { new_password: '', confirm: '' };

export default function AdminUsers() {
    const { getAuthHeaders } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState(EMPTY_USER);
    const [createLoading, setCreateLoading] = useState(false);

    const [pwTarget, setPwTarget] = useState(null); // { id, name }
    const [pwForm, setPwForm] = useState(EMPTY_PW);
    const [pwLoading, setPwLoading] = useState(false);

    const fetchUsers = async () => {
        try {
            const response = await axios.get(`${API}/admin/users`, { headers: getAuthHeaders() });
            setUsers(response.data);
        } catch {
            toast.error('Nepodařilo se načíst uživatele');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchUsers(); }, [getAuthHeaders]);

    const updateUserStatus = async (userId, subscriptionStatus) => {
        try {
            await axios.put(`${API}/admin/users/${userId}`, { subscription_status: subscriptionStatus }, { headers: getAuthHeaders() });
            toast.success('Uživatel byl aktualizován');
            fetchUsers();
        } catch {
            toast.error('Nepodařilo se aktualizovat uživatele');
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        if (createForm.password.length < 8) {
            toast.error('Heslo musí mít alespoň 8 znaků');
            return;
        }
        setCreateLoading(true);
        try {
            await axios.post(`${API}/admin/users`, createForm, { headers: getAuthHeaders() });
            toast.success('Uživatel byl vytvořen');
            setCreateOpen(false);
            setCreateForm(EMPTY_USER);
            fetchUsers();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Nepodařilo se vytvořit uživatele');
        } finally {
            setCreateLoading(false);
        }
    };

    const handleSetPassword = async (e) => {
        e.preventDefault();
        if (pwForm.new_password !== pwForm.confirm) {
            toast.error('Hesla se neshodují');
            return;
        }
        if (pwForm.new_password.length < 8) {
            toast.error('Heslo musí mít alespoň 8 znaků');
            return;
        }
        setPwLoading(true);
        try {
            await axios.put(`${API}/admin/users/${pwTarget.id}/password`, { new_password: pwForm.new_password }, { headers: getAuthHeaders() });
            toast.success('Heslo bylo změněno');
            setPwTarget(null);
            setPwForm(EMPTY_PW);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Nepodařilo se změnit heslo');
        } finally {
            setPwLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
            trialing: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
            past_due: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
            canceled: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
            inactive: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
        };
        const labels = {
            active: 'Aktivní', trialing: 'Zkušební', past_due: 'Nezaplaceno',
            canceled: 'Zrušeno', inactive: 'Neaktivní'
        };
        return (
            <span className={`px-2 py-1 rounded-full text-xs ${styles[status] || styles.inactive}`}>
                {labels[status] || 'Neaktivní'}
            </span>
        );
    };

    const inputCls = "w-full px-3 py-2 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-primary";

    return (
        <div data-testid="admin-users">
            <div className="flex items-center justify-between mb-8">
                <h1 className="font-heading text-2xl md:text-3xl font-semibold">Správa uživatelů</h1>
                <div className="flex items-center gap-4">
                    <p className="text-muted-foreground">{users.length} uživatelů</p>
                    <Button className="listarna-btn-primary flex items-center gap-2" onClick={() => setCreateOpen(true)}>
                        <UserPlus className="w-4 h-4" />
                        Vytvořit uživatele
                    </Button>
                </div>
            </div>

            {/* Users Table */}
            <div className="listarna-card rounded-lg overflow-hidden">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Jméno</th>
                            <th>E-mail</th>
                            <th>Role</th>
                            <th>Předplatné</th>
                            <th>Registrace</th>
                            <th className="text-right">Akce</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={6} className="text-center py-8"><div className="animate-pulse">Načítání...</div></td></tr>
                        ) : users.length === 0 ? (
                            <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">Žádní uživatelé</td></tr>
                        ) : (
                            users.map((u) => (
                                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                                    <td className="font-medium">{u.name}</td>
                                    <td className="text-muted-foreground">{u.email}</td>
                                    <td>
                                        <span className={`px-2 py-1 rounded-full text-xs ${u.role === 'admin' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'}`}>
                                            {u.role === 'admin' ? 'Admin' : 'Uživatel'}
                                        </span>
                                    </td>
                                    <td>{getStatusBadge(u.subscription_status)}</td>
                                    <td className="text-muted-foreground">{new Date(u.created_at).toLocaleDateString('cs-CZ')}</td>
                                    <td className="text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                onClick={() => { setPwTarget({ id: u.id, name: u.name }); setPwForm(EMPTY_PW); }}
                                                className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                                                title="Nastavit heslo"
                                            >
                                                <KeyRound className="w-4 h-4" />
                                            </button>
                                            <Select
                                                value={u.subscription_status || 'inactive'}
                                                onValueChange={(value) => updateUserStatus(u.id, value)}
                                            >
                                                <SelectTrigger className="w-32" data-testid={`user-status-select-${u.id}`}>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="inactive">Neaktivní</SelectItem>
                                                    <SelectItem value="trialing">Zkušební</SelectItem>
                                                    <SelectItem value="active">Aktivní</SelectItem>
                                                    <SelectItem value="past_due">Nezaplaceno</SelectItem>
                                                    <SelectItem value="canceled">Zrušeno</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Create User Modal */}
            {createOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-background rounded-lg p-6 w-full max-w-md shadow-xl">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="font-heading text-xl font-semibold">Vytvořit uživatele</h2>
                            <button onClick={() => setCreateOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
                        </div>
                        <form onSubmit={handleCreateUser} className="space-y-4">
                            <div>
                                <label className="text-sm font-medium text-muted-foreground mb-1 block">Jméno</label>
                                <input className={inputCls} value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} required />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-muted-foreground mb-1 block">E-mail</label>
                                <input type="email" className={inputCls} value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} required />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-muted-foreground mb-1 block">Heslo (min. 8 znaků)</label>
                                <input type="password" className={inputCls} value={createForm.password} onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))} required minLength={8} autoComplete="new-password" />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-muted-foreground mb-1 block">Role</label>
                                <select className={inputCls} value={createForm.role} onChange={e => setCreateForm(f => ({ ...f, role: e.target.value }))}>
                                    <option value="user">Uživatel</option>
                                    <option value="admin">Administrátor</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-muted-foreground mb-1 block">Předplatné</label>
                                <select className={inputCls} value={createForm.subscription_status} onChange={e => setCreateForm(f => ({ ...f, subscription_status: e.target.value }))}>
                                    <option value="inactive">Neaktivní</option>
                                    <option value="trialing">Zkušební</option>
                                    <option value="active">Aktivní</option>
                                </select>
                            </div>
                            <div className="flex gap-3 pt-2">
                                <Button type="button" variant="outline" className="flex-1" onClick={() => setCreateOpen(false)}>Zrušit</Button>
                                <Button type="submit" className="listarna-btn-primary flex-1" disabled={createLoading}>
                                    {createLoading ? 'Vytvářím...' : 'Vytvořit'}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Set Password Modal */}
            {pwTarget && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-background rounded-lg p-6 w-full max-w-sm shadow-xl">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="font-heading text-xl font-semibold">Nastavit heslo</h2>
                            <button onClick={() => setPwTarget(null)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
                        </div>
                        <p className="text-sm text-muted-foreground mb-4">Uživatel: <strong>{pwTarget.name}</strong></p>
                        <form onSubmit={handleSetPassword} className="space-y-4">
                            <div>
                                <label className="text-sm font-medium text-muted-foreground mb-1 block">Nové heslo (min. 8 znaků)</label>
                                <input type="password" className={inputCls} value={pwForm.new_password} onChange={e => setPwForm(f => ({ ...f, new_password: e.target.value }))} required minLength={8} autoComplete="new-password" />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-muted-foreground mb-1 block">Potvrzení hesla</label>
                                <input type="password" className={inputCls} value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} required minLength={8} autoComplete="new-password" />
                            </div>
                            <div className="flex gap-3 pt-2">
                                <Button type="button" variant="outline" className="flex-1" onClick={() => setPwTarget(null)}>Zrušit</Button>
                                <Button type="submit" className="listarna-btn-primary flex-1" disabled={pwLoading}>
                                    {pwLoading ? 'Ukládám...' : 'Uložit heslo'}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
